import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { AiAndRandom, axiosInstance, BrollCaptionsAndRenderType, StockLibrary } from '../../utils';
import { Broll } from '@t2c/ai-video-editor';
import { BrollCaptionVideo } from '../../../t2c-fe/@types';
export interface FetchBrollCaptionParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: {
    renderStatus?: string;
    status?: string;
  };
  model?: BrollCaptionsAndRenderType;
  onlySuccess?: boolean;
}

interface BrollCaptionResponse {
  brollCaptionVideos: BrollCaptionVideo[];
  videosTotalCount: number;
  recentVideosCount: number;
}

export const fetchBrollCaptionData = async ({
  page = 1,
  limit = 100,
  sortBy = 'updatedAt',
  sortOrder = 'desc',
  filters = {},
  model = BrollCaptionsAndRenderType.REMOTION,
  onlySuccess 
}: FetchBrollCaptionParams): Promise<BrollCaptionResponse> => {
  const searchParams = new URLSearchParams();

  if (page) searchParams.append('page', page.toString());
  if (limit) searchParams.append('limit', limit.toString());
  if (sortBy) searchParams.append('sortBy', sortBy);
  if (sortOrder) searchParams.append('sortOrder', sortOrder);
  if (model) searchParams.append('model', model);
  if (onlySuccess) searchParams.append('onlySuccess', onlySuccess.toString());

  // Append filters to query parameters
  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      searchParams.append(`filters[${key}]`, value);
    }
  });

  const response = await axiosInstance.get(`/api/video/all?${searchParams.toString()}`);
  return response.data;
};

// React Query hook with dynamic parameters
export const useFetchAllCreatedBrollCaptions = (
  params?: FetchBrollCaptionParams, 
  model: BrollCaptionsAndRenderType = BrollCaptionsAndRenderType.REMOTION, 
  onlySuccess: boolean = false
) => {
  return useInfiniteQuery(
    ['fetchAllCreatedBrollCaptions', params, model, onlySuccess],
    ({ pageParam = 1 }) => fetchBrollCaptionData({ ...params, page: pageParam, model: model, onlySuccess: onlySuccess }),
    {
      getNextPageParam: (lastPage, allPages) => {
        const currentPage = allPages.length;
        const totalItemsFetched = currentPage * (params?.limit ?? 20);

        if (totalItemsFetched < lastPage.videosTotalCount) {
          return currentPage + 1;
        } else {
          return undefined;
        }
      },
    }
  );
};

export interface AIBrollPayload {
  magicBrollPercent: number;
  brollData: Broll[];
  galleryServiceName: StockLibrary;
  brollApplyOption: AiAndRandom;
}

export interface AIBrollResponseType {
  jsonId: string;
}

export const getAIBrolls = async (payload: AIBrollPayload): Promise<AIBrollResponseType> => {
  const res = await axiosInstance.post('/api/broll/ai-broll', payload);
  return res.data;
};

export interface StockBrollPayload {
  keyword: string;
  category: string;
  pageSize?: number;
  pageNumber?: number;
  galleryServiceName?: string | 'pexels';
}
export interface StockBrollItem {
  thumbnailUrl: string;
  url: string;
}

export interface StockBrollResponse {
  pageNumber: number;
  pageSize: number;
  totalResultSize: number;
  items: StockBrollItem[];
}

export const getStockBrolls = async (payload: StockBrollPayload): Promise<StockBrollResponse> => {
  // Construct the query string using the payload properties
  const { keyword, category, pageSize, pageNumber, galleryServiceName } = payload;

  // Build the query parameters dynamically
  const queryParams = new URLSearchParams({
    keyword,
    category,
    ...(galleryServiceName && { galleryServiceName: galleryServiceName.toString() }),
    ...(pageSize && { pageSize: pageSize.toString() }), // Include pageSize if provided
    ...(pageNumber && { pageNumber: pageNumber.toString() }), // Include pageNumber if provided
  });

  try {
    // Make the API call with the constructed query string
    const res = await axiosInstance.get(`/api/stock-gallery/search?${queryParams.toString()}`);
    return res.data;
  } catch (error) {
    console.error('Error fetching stock brolls:', error);
    throw error;
  }
};
